import { v4 as uuidv4 } from 'uuid';

// Генерация UUID с префиксом
export function generateUUID(prefix = 'item') {
  const timestamp = Date.now();
  const randomPart = Math.random().toString(36).substring(2, 11);
  return `${prefix}-${timestamp}-${randomPart}`;
}

// Создание пустого Stage
export function createEmptyStage(stageNumber) {
  return {
    UUID: generateUUID('stage'),
    Stage_Name: `Новый этап ${stageNumber}`,
    Stage_Number: String(stageNumber),
    Stage_Description: '',
    Skip_If: '',
    Stage_Vars: [createEmptyStageVar(stageNumber, 1)]
  };
}

// Создание пустого Stage Variant
export function createEmptyStageVar(stageNumber, variantNumber) {
  return {
    UUID: generateUUID('stage-variant'),
    Name_ID: `Stage${stageNumber}_V${variantNumber}`,
    Stage_Name: `Вариант ${variantNumber}`,
    Choose_If: '',
    Steps: [createEmptyStep(stageNumber, variantNumber, 1)]
  };
}

// Создание пустого Step
export function createEmptyStep(stageNumber, variantNumber, stepNumber) {
  return {
    UUID: generateUUID('step'),
    Name_ID: `Stage${stageNumber}_V${variantNumber}_Step${stepNumber}`,
    Step_Name: `Шаг ${stepNumber}`,
    Step_Number: String(stepNumber),
    Step_Description: '',
    Skip_If: '',
    Step_Vars: [createEmptyStepVar(stageNumber, variantNumber, stepNumber, 1)]
  };
}

// Создание пустого Step Variant
export function createEmptyStepVar(stageNumber, variantNumber, stepNumber, stepVarNumber) {
  return {
    Name_ID: `Stage${stageNumber}_V${variantNumber}_Step${stepNumber}_V${stepVarNumber}`,
    Var_UUID: generateUUID('step-var'),
    Choose_If: '',
    Step_Var_Name: `Вариант ${stepVarNumber}`,
    Task_Settings: {
      Task: '',
      Status: '',
      Qual_Tags: [],
      Templates: [],
      Modul_Tags: [],
      Time_Wait_CL_Reply: '',
      Group_Followup_Or_Stop_If_No_Reply: ''
    }
  };
}

// Поиск Stage по UUID
export function findStageByUUID(script, uuid) {
  if (!script?.Main_Script?.Stages) return null;
  return script.Main_Script.Stages.find(stage => stage.UUID === uuid);
}

// Поиск Step по UUID
export function findStepByUUID(script, uuid) {
  if (!script?.Main_Script?.Stages) return null;
  
  for (const stage of script.Main_Script.Stages) {
    for (const variant of stage.Stage_Vars || []) {
      const step = variant.Steps?.find(step => step.UUID === uuid);
      if (step) return { step, stage, variant };
    }
  }
  return null;
}

// Получение всех этапов для навигатора
export function getAllStagesForNavigator(script) {
  if (!script?.Main_Script?.Stages) return [];
  
  const items = [];
  
  script.Main_Script.Stages.forEach((stage, stageIdx) => {
    items.push({
      type: 'stage',
      uuid: stage.UUID,
      label: `${stage.Stage_Number}. ${stage.Stage_Name}`,
      index: stageIdx
    });
    
    stage.Stage_Vars?.forEach((variant, variantIdx) => {
      // Формируем название варианта
      const variantCount = stage.Stage_Vars.length;
      const variantLabel = variantCount > 1 
        ? `Вариант ${variantIdx + 1}${variant.Stage_Name ? `: ${variant.Stage_Name}` : ''}` 
        : (variant.Stage_Name || 'Вариант');
      
      variant.Steps?.forEach((step, stepIdx) => {
        items.push({
          type: 'step',
          uuid: step.UUID,
          label: `  └─ [${variantLabel}] Шаг ${step.Step_Number}: ${step.Step_Name}`,
          index: { stageIdx, variantIdx, stepIdx }
        });
      });
    });
  });
  
  return items;
}

// Подсчет символов в тексте
export function countCharacters(text) {
  return text ? text.length : 0;
}

// Валидация лимитов
export function validateCharLimit(text, limit) {
  const count = countCharacters(text);
  return {
    count,
    limit,
    isValid: count <= limit,
    percentage: (count / limit) * 100
  };
}

// Клонирование Stage
export function cloneStage(stage, newStageNumber) {
  const cloned = JSON.parse(JSON.stringify(stage));
  cloned.UUID = generateUUID('stage');
  cloned.Stage_Number = String(newStageNumber);
  cloned.Stage_Name = `${stage.Stage_Name} (копия)`;
  
  // Обновляем UUID для всех вложенных элементов
  cloned.Stage_Vars = cloned.Stage_Vars?.map(variant => ({
    ...variant,
    UUID: generateUUID('stage-variant'),
    Steps: variant.Steps?.map(step => ({
      ...step,
      UUID: generateUUID('step'),
      Step_Vars: step.Step_Vars?.map(stepVar => ({
        ...stepVar,
        Var_UUID: generateUUID('step-var')
      }))
    }))
  }));
  
  return cloned;
}

// Клонирование Step
export function cloneStep(step, newStepNumber) {
  const cloned = JSON.parse(JSON.stringify(step));
  cloned.UUID = generateUUID('step');
  cloned.Step_Number = String(newStepNumber);
  cloned.Step_Name = `${step.Step_Name} (копия)`;
  
  cloned.Step_Vars = cloned.Step_Vars?.map(stepVar => ({
    ...stepVar,
    Var_UUID: generateUUID('step-var')
  }));
  
  return cloned;
}

// Получение следующего номера Stage
export function getNextStageNumber(script) {
  if (!script?.Main_Script?.Stages || script.Main_Script.Stages.length === 0) {
    return 1;
  }
  
  const numbers = script.Main_Script.Stages
    .map(s => parseInt(s.Stage_Number))
    .filter(n => !isNaN(n));
  
  return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
}

// Получение следующего номера Step в варианте
export function getNextStepNumber(variant) {
  if (!variant?.Steps || variant.Steps.length === 0) {
    return 1;
  }
  
  const numbers = variant.Steps
    .map(s => parseInt(s.Step_Number))
    .filter(n => !isNaN(n));
  
  return numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
}
